import { useState, useCallback, useEffect, useMemo } from 'react';
import { OutcomeProcessor } from '../services/OutcomeProcessor';
import * as api from '../../../api/api';
import { isCacheClearNeeded, markCachesCleared } from '../../../utils/cacheInvalidation';

/**
 * Custom hook for managing forecast data fetching and processing
 * Preserves all existing data fetching logic while organizing it better
 */
export const useForecastData = (selectedPlanId, year, planData = null, department = null) => {
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0, isProcessing: false });
  const [debugInfo, setDebugInfo] = useState({ fetchAttempts: 0, lastFetchTime: null });
  const [lastDependencies, setLastDependencies] = useState({ selectedPlanId: null, year: null });
  const [serverStatus, setServerStatus] = useState({ connected: true, lastCheck: null });
  
  // Create outcome processor instance
  const outcomeProcessor = useMemo(() => new OutcomeProcessor(), []);

  /**
   * Get outcomes to process based on selected plan
   * PRESERVES ALL EXISTING PLAN LOGIC
   */
  const getOutcomesToProcess = useCallback(async (planId) => {
    let outcomesToProcess = [];

    if (planId) {
      try {
        let plan;
        if (planData?.id === planId) {
          plan = planData;
          if (process.env.NODE_ENV === 'development') {
            console.log('Using pre-supplied plan data:', plan.name);
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetching plan:', planId);
          }
          const planResponse = await api.getPlan(planId);
          plan = planResponse.data || planResponse;
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Plan loaded:', plan.name, 'with', plan.selected_outcomes?.length || plan.outcomes?.length || 0, 'outcomes');
        }
        
        if (plan.selected_outcomes && Array.isArray(plan.selected_outcomes) && plan.selected_outcomes.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('📋 Plan has selected_outcomes:', plan.selected_outcomes.length, 'IDs:', plan.selected_outcomes);
          }
          
          const BATCH_SIZE = 8;
          const outcomeResults = [];
          
          for (let i = 0; i < plan.selected_outcomes.length; i += BATCH_SIZE) {
            const batch = plan.selected_outcomes.slice(i, i + BATCH_SIZE);
            const batchPromises = batch.map(outcomeId => 
              api.getOutcome(outcomeId).catch(err => {
                console.warn(`Failed to fetch outcome ${outcomeId}:`, err);
                return null;
              })
            );
            const batchResults = await Promise.all(batchPromises);
            outcomeResults.push(...batchResults);
          }
          
          outcomesToProcess = outcomeResults
            .filter(response => response !== null)
            .map(response => response.data || response)
            .filter(outcome => outcome && !outcome.deleted);
            
          if (process.env.NODE_ENV === 'development') {
            console.log('📋 Fetched', outcomesToProcess.length, 'outcomes from selected_outcomes');
            if (outcomesToProcess.length === 0) {
              console.warn('⚠️ No valid outcomes found from plan.selected_outcomes - this might be the issue!');
            }
          }
        } else if (plan.outcomes && Array.isArray(plan.outcomes) && plan.outcomes.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('📋 Using plan.outcomes:', plan.outcomes.length);
          }
          outcomesToProcess = plan.outcomes;
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Plan has no selected_outcomes or outcomes:', {
              selected_outcomes: plan.selected_outcomes,
              outcomes: plan.outcomes,
              planKeys: Object.keys(plan)
            });
          }
          outcomesToProcess = [];
        }
      } catch (planError) {
        console.error('❌ Error fetching plan, falling back to all outcomes:', planError);
        const response = await api.getOutcomes(department);
        const allOutcomes = response.outcomes || response || [];
        if (process.env.NODE_ENV === 'development') {
          console.log('📋 Fallback: Fetched', allOutcomes.length, 'total outcomes from API');
        }
        outcomesToProcess = allOutcomes.filter(outcome => 
          ['Approved', 'Complete', 'approved', 'complete'].includes(outcome.status) && !outcome.deleted
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('📋 Fallback: Filtered to', outcomesToProcess.length, 'approved/complete outcomes');
        }
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('No plan selected, fetching all approved/complete outcomes');
      }
      const response = await api.getOutcomes(department);
      const allOutcomes = response.outcomes || response || [];
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 No plan: Fetched', allOutcomes.length, 'total outcomes from API');
        console.log('📋 No plan: Outcome statuses:', allOutcomes.map(o => ({ name: o.name, status: o.status, deleted: o.deleted })));
      }
      outcomesToProcess = allOutcomes.filter(outcome => 
        ['Approved', 'Complete', 'approved', 'complete'].includes(outcome.status) && !outcome.deleted
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 No plan: Filtered to', outcomesToProcess.length, 'approved/complete outcomes');
        if (outcomesToProcess.length === 0) {
          console.warn('⚠️ No approved/complete outcomes found! This is likely the issue.');
          console.log('📋 Available statuses:', [...new Set(allOutcomes.map(o => o.status))]);
        }
      }
    }

    return outcomesToProcess;
  }, [planData]);

  /**
   * Main data fetching function
   * PRESERVES ALL EXISTING FETCH LOGIC WITH BATCHING AND PROGRESS
   */
  const fetchData = useCallback(async () => {
    const fetchStartTime = Date.now();
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 ForecastVsActuals: Starting fetchData at', new Date().toLocaleTimeString());
      }
      
      setDebugInfo(prev => ({ 
        fetchAttempts: prev.fetchAttempts + 1, 
        lastFetchTime: new Date().toISOString() 
      }));
      
      setLoading(true);
      setError('');

      if (isCacheClearNeeded()) {
        outcomeProcessor.clearCache();
        api.clearStaffingCache();
        api.clearSpendCache();
        markCachesCleared();
      }

      const startTime = performance.now();

      // Get outcomes to process
      const outcomesToProcess = await getOutcomesToProcess(selectedPlanId);

      // Inject outcomes that have uploaded spend data but are not in the plan
      if (selectedPlanId) {
        try {
          const planOutcomeIds = new Set(outcomesToProcess.map(o => o.id));
          const uploadedIds = await api.getUploadedOutcomeIds(year);
          const missingIds = uploadedIds.filter(id => !planOutcomeIds.has(id));
          if (missingIds.length > 0) {
            const fetchPromises = missingIds.map(id =>
              api.getOutcome(id).catch(() => null)
            );
            const fetchResults = await Promise.all(fetchPromises);
            fetchResults.forEach(response => {
              if (!response) return;
              const outcome = response.data || response;
              if (outcome && !outcome.deleted) {
                outcomesToProcess.push({ ...outcome, isOutOfPlan: true });
              }
            });
          }
        } catch (e) {
          // Non-fatal — missing out-of-plan outcomes just won't appear
        }
      }

        // Inject synthetic "Other (Unmatched Projects)" outcome if unmatched upload data exists
      try {
        const unmatchedSpend = await api.getSpendData('_unmatched', year);
        const hasUnmatchedSpend = unmatchedSpend?.totalActual > 0;
        const hasUnmatchedForecast = unmatchedSpend?.officialForecast?.totalForecast > 0;
        if (hasUnmatchedSpend || hasUnmatchedForecast) {
          outcomesToProcess.push({
            id: '_unmatched',
            name: 'Other (Unmatched Projects)',
            project_code: '—',
            status: 'Approved',
            deleted: false,
            isSynthetic: true,
            description: 'Amounts from uploaded files with no matching outcome project code',
            unmatchedSpendDetails: unmatchedSpend.unmatched_project_details || [],
            unmatchedForecastDetails: unmatchedSpend.officialForecast?.unmatched_project_details || [],
          });
          if (process.env.NODE_ENV === 'development') {
            console.log(`📦 Injecting _unmatched synthetic outcome — spend: $${unmatchedSpend.totalActual}, forecast: $${unmatchedSpend.officialForecast?.totalForecast}`);
          }
        }
      } catch (e) {
        // No unmatched data — normal case when no uploads have been made
      }

      // Pre-fetch all spend data in one bulk call — populates module cache for processing loop
      try {
        const bulkIds = outcomesToProcess.filter(o => o.id !== '_unmatched').map(o => o.id);
        if (bulkIds.length > 0) await api.getSpendDataBulk(bulkIds, year);
      } catch (e) { /* non-fatal — processing loop falls back to individual calls */ }

      if (process.env.NODE_ENV === 'development' && outcomesToProcess.length > 5) {
        console.log('Processing', outcomesToProcess.length, 'outcomes');
      }

      if (outcomesToProcess.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ No outcomes to process, setting empty state');
        }
        setOutcomes([]);
        setLoading(false);
        setProcessingProgress({ current: 0, total: 0, isProcessing: false });
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Starting to process', outcomesToProcess.length, 'outcomes');
      }

      setProcessingProgress({ current: 0, total: outcomesToProcess.length, isProcessing: true });

      // Process in batches - PRESERVES EXISTING BATCH LOGIC
      const PROCESSING_BATCH_SIZE = 5;
      const processedOutcomes = [];
      
      for (let i = 0; i < outcomesToProcess.length; i += PROCESSING_BATCH_SIZE) {
        const batch = outcomesToProcess.slice(i, i + PROCESSING_BATCH_SIZE);
        
        const currentProgress = Math.min(i + PROCESSING_BATCH_SIZE, outcomesToProcess.length);
        setProcessingProgress({ 
          current: currentProgress, 
          total: outcomesToProcess.length, 
          isProcessing: true 
        });
        
        if (process.env.NODE_ENV === 'development' && outcomesToProcess.length > 20 && currentProgress % 10 === 0) {
          console.log(`📈 Processing progress: ${currentProgress}/${outcomesToProcess.length} (${Math.round(currentProgress/outcomesToProcess.length*100)}%)`);
        }
        
        const batchResults = await Promise.all(
          batch.map(outcome => outcomeProcessor.processOutcome(outcome, year))
        );
        
        processedOutcomes.push(...batchResults);
        setOutcomes([...processedOutcomes]);

        if (i + PROCESSING_BATCH_SIZE < outcomesToProcess.length) {
          await new Promise(resolve => setTimeout(resolve, 5));
        }
      }
      
      setProcessingProgress({ current: outcomesToProcess.length, total: outcomesToProcess.length, isProcessing: false });

      if (process.env.NODE_ENV === 'development') {
        const endTime = performance.now();
        const totalTime = endTime - startTime;
        const avgTimePerOutcome = totalTime / processedOutcomes.length;
        const withCompanyData = processedOutcomes.filter(o => Object.keys(o.companyBreakdown || {}).length > 0).length;
        const withActualSpend = processedOutcomes.filter(o => o.totalActualSpend > 0).length;
        
        console.log(`✅ Processed ${processedOutcomes.length} outcomes in ${totalTime.toFixed(0)}ms (avg: ${avgTimePerOutcome.toFixed(0)}ms/outcome)`);
        console.log(`📊 Data quality: ${withCompanyData} with company data, ${withActualSpend} with actual spend`);
        
        if (processedOutcomes.length > 50 && avgTimePerOutcome > 500) {
          console.warn(`⚠️ Performance: Processing ${processedOutcomes.length} outcomes took ${avgTimePerOutcome.toFixed(0)}ms per outcome (consider optimization)`);
        }
        
        const cacheStats = outcomeProcessor.getCacheStats();
        const cacheHitRate = cacheStats.size > 0 ? ((processedOutcomes.length - cacheStats.size) / processedOutcomes.length * 100).toFixed(1) : '0';
        console.log(`🗄️ Cache efficiency: ${cacheHitRate}% hit rate (${cacheStats.size} cached entries)`);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Setting', processedOutcomes.length, 'processed outcomes and completing load');
      }
      
      setOutcomes(processedOutcomes);
      setLoading(false);
      setProcessingProgress({ current: 0, total: 0, isProcessing: false });
      
    } catch (error) {
      const fetchEndTime = Date.now();
      const fetchDuration = fetchEndTime - fetchStartTime;
      
      console.error('❌ Error fetching outcomes after', fetchDuration, 'ms:', error);
      setError(`Failed to load outcomes: ${error.message}`);
      setLoading(false);
      setProcessingProgress({ current: 0, total: 0, isProcessing: false });
    }
  }, [selectedPlanId, year, department, getOutcomesToProcess, outcomeProcessor]);

  /**
   * Network connectivity check - PRESERVES EXISTING LOGIC
   */
  const checkNetworkConnectivity = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/health`, {
        method: 'GET',
        timeout: 5000
      });
      const isConnected = response.ok;
      setServerStatus({ connected: isConnected, lastCheck: new Date().toISOString() });
      return isConnected;
    } catch (error) {
      console.warn('⚠️ Network connectivity check failed:', error);
      setServerStatus({ connected: false, lastCheck: new Date().toISOString() });
      return false;
    }
  }, []);

        /**
   * Clear cache and refresh data
   */
  const clearCacheAndRefresh = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 clearCacheAndRefresh: Clearing all caches and refreshing data');
    }
    
    // Clear outcome processor cache
    outcomeProcessor.clearCache();
    api.clearStaffingCache();
    api.clearSpendCache();

    // Clear browser caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          if (name.includes('spend') || name.includes('forecast') || name.includes('outcome')) {
            caches.delete(name);
            if (process.env.NODE_ENV === 'development') {
              console.log('🧹 Cleared browser cache:', name);
            }
          }
        });
      }).catch(() => {}); // Ignore cache errors
    }
    
    // Reset all state
    setProcessingProgress({ current: 0, total: 0, isProcessing: false });
    setOutcomes([]);
    setError('');
    
    // Force a re-fetch by setting forcedRefresh flag
    setDebugInfo(prev => ({ 
      ...prev,
      forcedRefresh: true,
      lastFetchTime: new Date().toISOString()
    }));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 clearCacheAndRefresh: Triggering data refresh via forcedRefresh flag');
    }
  }, [outcomeProcessor]);

  // Auto-recovery mechanism for stuck loading states - PRESERVES EXISTING LOGIC
  useEffect(() => {
    if (loading && debugInfo.lastFetchTime) {
      const timeoutId = setTimeout(() => {
        const loadingTime = Date.now() - new Date(debugInfo.lastFetchTime).getTime();
        console.warn('⏰ Auto-recovery triggered after', loadingTime, 'ms of loading');
        setLoading(false);
        setProcessingProgress({ current: 0, total: 0, isProcessing: false });
        setError('Loading timed out. Please try the manual refresh button or check your network connection.');
      }, 90000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading, debugInfo.lastFetchTime]);

  // Periodic server connectivity check - PRESERVES EXISTING LOGIC
  useEffect(() => {
    checkNetworkConnectivity();
    
    const connectivityInterval = setInterval(() => {
      checkNetworkConnectivity();
    }, 120000); // 2 minutes
    
    return () => clearInterval(connectivityInterval);
  }, [checkNetworkConnectivity]);

            // Initial data fetch when selectedPlanId or year changes
  useEffect(() => {
    // Reset fetch attempts when actual dependencies change (not internal state)
    if (lastDependencies.selectedPlanId !== selectedPlanId || lastDependencies.year !== year || lastDependencies.department !== department) {
      setLastDependencies({ selectedPlanId, year, department });
      setDebugInfo(prev => ({ ...prev, fetchAttempts: 0 }));

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 useForecastData: Dependencies changed, triggering fetchData', {
          selectedPlanId,
          year,
          department
        });
      }
      fetchData();
    }
  }, [selectedPlanId, year, department, fetchData, lastDependencies.selectedPlanId, lastDependencies.year, lastDependencies.department]);

  // Separate effect for handling forced refresh and circuit breaker
  useEffect(() => {
    // Circuit breaker to prevent infinite loops
    if (debugInfo.fetchAttempts > 10) {
      console.error('🚨 Circuit breaker activated: Too many fetch attempts (', debugInfo.fetchAttempts, '). Stopping to prevent infinite loop.');
      setError('Too many fetch attempts. Please refresh the page.');
      setLoading(false);
      return;
    }
    
    // Only trigger fetchData if it's a forced refresh or event-triggered
    if (debugInfo.forcedRefresh || debugInfo.eventTriggered) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 useForecastData: Triggering fetchData due to forced refresh or event', { 
          forcedRefresh: debugInfo.forcedRefresh,
          eventTriggered: debugInfo.eventTriggered,
          fetchAttempts: debugInfo.fetchAttempts 
        });
      }
      
      // Clear the flags to prevent repeated triggers
      setDebugInfo(prev => ({ 
        ...prev, 
        forcedRefresh: false, 
        eventTriggered: false 
      }));
      
      fetchData();
    }
  }, [debugInfo.fetchAttempts, debugInfo.forcedRefresh, debugInfo.eventTriggered, fetchData]);

  // Enhanced event listeners for silent refresh integration - PRESERVES EXISTING LOGIC
  useEffect(() => {
        const handleOutcomeRatesUpdated = (event) => {
      const { outcomeIds, updateDetails, source } = event.detail;
      
      const hasAffectedOutcomes = outcomes.some(outcome => 
        outcomeIds.includes(outcome.id)
      );
      
      if (hasAffectedOutcomes) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Refreshing data due to outcome rate updates for', outcomeIds.length, 'outcomes from', source || 'unknown');
        }
        
        outcomeProcessor.clearCache();
        api.clearStaffingCache(outcomeIds);

        const delay = source === 'roster_silent_refresh' || source === 'staffing_silent_refresh' ? 500 : 1000;
        setTimeout(() => {
          setDebugInfo(prev => ({ 
            ...prev,
            eventTriggered: true,
            lastFetchTime: new Date().toISOString()
          }));
        }, delay);
      }
    };
    
    const handleOutcomeCachesInvalidated = (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Received cache invalidation event');
      }
      
      clearCacheAndRefresh();
    };
    
        if (typeof window !== 'undefined') {
      window.addEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
      window.addEventListener('outcomeCachesInvalidated', handleOutcomeCachesInvalidated);
      
      return () => {
        window.removeEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
        window.removeEventListener('outcomeCachesInvalidated', handleOutcomeCachesInvalidated);
      };
    }
  }, [outcomes, outcomeProcessor, clearCacheAndRefresh]);

  return {
    outcomes,
    loading,
    error,
    processingProgress,
    debugInfo,
    serverStatus,
    refetch: fetchData,
    clearCacheAndRefresh,
    checkNetworkConnectivity
  };
};